import {
  AUTH_API_ENDPOINT,
  CAMPAIGNS_API_ENDPOINT,
} from "../../../constants/constant";
import baseApi from "../../baseApi";
import { ApiResponse } from "../campaigns/campaigns.type";
import { ResultType } from "../users/user.type";
import {
  Log,
  Notification,
  OtpPayload,
  Profile,
  Refferal,
  ResetPasswordPayload,
  SignInPayload,
  UseQueryParams,
} from "./auth.type";

export const authApiSlice = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTokenPriceConversionApiCall: build.query<ApiResponse<any>, void>({
      query: () => ({
        url: CAMPAIGNS_API_ENDPOINT.getBinGoldPrice,
        method: "GET",
      }),
    }),
    signInApiCall: build.mutation<any, SignInPayload>({
      query: (signInBody) => ({
        url: AUTH_API_ENDPOINT.SIGNIN,
        method: "POST",
        body: signInBody,
      }),
    }),

    sendOtpApiCall: build.mutation<any, OtpPayload>({
      query: (OtpBody) => ({
        url: AUTH_API_ENDPOINT.SEND_OTP,
        method: "POST",
        body: OtpBody,
      }),
    }),

    verifyOtpApiCall: build.mutation<any, OtpPayload>({
      query: (verifyOtpBody) => ({
        url: AUTH_API_ENDPOINT.VERIFY_OTP,
        method: "PATCH",
        body: verifyOtpBody,
      }),
    }),

    resendOtpApiCall: build.mutation<any, Omit<OtpPayload, "otp">>({
      query: (resendOtpBody) => ({
        url: AUTH_API_ENDPOINT.RESEND_OTP,
        method: "POST",
        body: resendOtpBody,
      }),
    }),

    forgotPasswordApiCall: build.mutation<any, { email: string }>({
      query: (forgotPasswordBody) => ({
        url: AUTH_API_ENDPOINT.FORGOT_PASSWORD,
        method: "POST",
        body: forgotPasswordBody,
      }),
    }),

    resetPasswordApiCall: build.mutation<any, ResetPasswordPayload>({
      query: (forgotPasswordBody) => ({
        url: AUTH_API_ENDPOINT.RESET_PASSWORD,
        method: "POST",
        body: forgotPasswordBody,
      }),
    }),

    getProfileApiCall: build.query<ApiResponse<Profile>, void>({
      query: () => ({
        url: AUTH_API_ENDPOINT.GET_PROFILE,
        method: "GET",
      }),
      providesTags: ["Profile"],
    }),

    updateProfileApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: AUTH_API_ENDPOINT.UPDATE_PROFILE,
        method: "PUT",
        body: profileBody,
      }),
      invalidatesTags: ["Profile"],
    }),

    deleteAccountApiCall: build.mutation<ApiResponse, any>({
      query: (deleteAccountBody) => ({
        url: `${AUTH_API_ENDPOINT.DELETE_ACCOUNT}?userId=${deleteAccountBody.userId}&reason=${deleteAccountBody.reason}`,
        method: "DELETE",
        // body: deleteAccountBody,
      }),
    }),

    getNotificationActivityApiCall: build.query<
      ApiResponse<ResultType<Log>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        return `${
          AUTH_API_ENDPOINT.GET_NOTIFICATIONS_ACTIVITY
        }?${queryParams.toString()}`;
      },
    }),

    getReadStatusNotificationApiCall: build.query<ApiResponse, void>({
      query: () => {
        return `${AUTH_API_ENDPOINT.GET_READ_STATUS_NOTIFICATION}`;
      },
    }),

    getListNotificationApiCall: build.query<
      ApiResponse<ResultType<Notification>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        return `${
          AUTH_API_ENDPOINT.GET_LIST_NOTIFICATIONS
        }?${queryParams.toString()}`;
      },
      providesTags: ["Notifications"],
    }),

    getReadNotificationApiCall: build.mutation<ApiResponse, void>({
      query: () => ({
        url: AUTH_API_ENDPOINT.GET_READ_NOTIFICATION,
        method: "PUT",
      }),
    }),

    deleteNotificationApiCall: build.mutation<ApiResponse, any>({
      query: (deleteBody) => ({
        url: `${AUTH_API_ENDPOINT.DELETE_NOTIFICATION}?_id=${deleteBody.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Notifications"],
    }),

    clearNotificationApiCall: build.mutation<ApiResponse, void>({
      query: () => ({
        url: AUTH_API_ENDPOINT.CLEAR_NOTIFICATION,
        method: "DELETE",
      }),
      invalidatesTags: ["Notifications"],
    }),

    getRefferalsApiCall: build.query<
      ApiResponse<ResultType<Refferal>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        return `${AUTH_API_ENDPOINT.GET_REFFERAL}`;
      },
    }),

    getRefferalUsersApiCall: build.query<
      ApiResponse<ResultType<Refferal>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        return `${
          AUTH_API_ENDPOINT.GET_REFFERAL_USER
        }?${queryParams.toString()}`;
      },
    }),

    // getRewardHistoryApiCall: build.query<
    //   ApiResponse<ResultType<RewardHistory>>,
    //   UseQueryParams | void
    // >({
    //   query: (params) => {
    //     const queryParams = new URLSearchParams();
    //     if (params?.page) queryParams.append("page", params.page.toString());
    //     if (params?.limit) queryParams.append("limit", params.limit.toString());
    //     return `${
    //       AUTH_API_ENDPOINT.GET_REWARD_HISTORY
    //     }?${queryParams.toString()}`;
    //   },
    // }),
  }),
});

export const {
  useGetTokenPriceConversionApiCallQuery,

  useSignInApiCallMutation,
  useVerifyOtpApiCallMutation,
  useSendOtpApiCallMutation,
  useResendOtpApiCallMutation,
  useUpdateProfileApiCallMutation,
  useResetPasswordApiCallMutation,
  useForgotPasswordApiCallMutation,
  useGetProfileApiCallQuery,
  useDeleteAccountApiCallMutation,
  useGetNotificationActivityApiCallQuery,
  useGetListNotificationApiCallQuery,
  useGetReadStatusNotificationApiCallQuery,
  useGetReadNotificationApiCallMutation,
  useDeleteNotificationApiCallMutation,
  useClearNotificationApiCallMutation,
  useGetRefferalsApiCallQuery,
  useGetRefferalUsersApiCallQuery,
} = authApiSlice;

export default authApiSlice;
